import React, { useContext, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { AuthContextWeb } from "../context/AuthContextWeb";

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PASSWORD_REGEX = /^(?=.*[A-Za-z])(?=.*\d).{8,}$/;

const initialPersonal = {
  fullName: "",
  email: "",
  password: "",
  confirmPassword: "",
  phone: "",
  address: "",
  dob: "",
};

const initialBusiness = {
  email: "",
  password: "",
  confirmPassword: "",
  businessName: "",
  businessAddress: "",
  registrationNumber: "",
  taxId: "",
};

const TABS = [
  { id: "renter", label: "Renter" },
  { id: "personal", label: "Personal Owner" },
  { id: "business", label: "Business Owner" },
];

const roleConfirmationCopy = {
  renter: {
    title: "Sign up as a renter?",
    description:
      "You are about to create a renter account for searching properties, saving listings, and contacting landlords.",
    confirmLabel: "Yes, sign up as renter",
  },
  personal: {
    title: "Sign up as a landlord?",
    description:
      "You are about to create a personal landlord account for listing and managing your own rental properties.",
    confirmLabel: "Yes, sign up as landlord",
  },
  business: {
    title: "Sign up as a landlord business?",
    description:
      "You are about to create a business landlord account for managing rental listings under a registered company.",
    confirmLabel: "Yes, sign up as business",
  },
};

export default function SignupPage() {
  const navigate = useNavigate();
  const {
    registerRenter,
    registerPersonal,
    registerBusiness,
    loading,
    error,
    clearError,
  } = useContext(AuthContextWeb);

  const [activeTab, setActiveTab] = useState("renter");
  const [personal, setPersonal] = useState(initialPersonal);
  const [business, setBusiness] = useState(initialBusiness);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [success, setSuccess] = useState("");
  const [localError, setLocalError] = useState("");
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);

  const onChangePersonal = (e) =>
    setPersonal((f) => ({ ...f, [e.target.name]: e.target.value }));

  const onChangeBusiness = (e) =>
    setBusiness((f) => ({ ...f, [e.target.name]: e.target.value }));

  const switchTab = (tab) => {
    setActiveTab(tab);
    clearError();
    setLocalError("");
    setSuccess("");
    setIsConfirmOpen(false);
  };

  const closeConfirmation = () => setIsConfirmOpen(false);

  const submitRegistration = async () => {
    try {
      let payload;
      if (activeTab === "renter") {
        payload = await registerRenter({
          fullName: personal.fullName.trim(),
          email: personal.email.trim().toLowerCase(),
          password: personal.password,
          phone: personal.phone.trim(),
          address: personal.address.trim(),
          dob: personal.dob || "2000-01-01",
        });
      } else if (activeTab === "personal") {
        payload = await registerPersonal({
          fullName: personal.fullName.trim(),
          email: personal.email.trim().toLowerCase(),
          password: personal.password,
          phone: personal.phone.trim(),
          address: personal.address.trim(),
          dob: personal.dob || "2000-01-01",
        });
      } else {
        payload = await registerBusiness({
          email: business.email.trim().toLowerCase(),
          password: business.password,
          businessName: business.businessName.trim(),
          businessAddress: business.businessAddress.trim(),
          registrationNumber: business.registrationNumber.trim(),
          taxId: business.taxId.trim(),
        });
      }

      closeConfirmation();
      setSuccess(
        payload?.message ||
          "Registration complete. Check your email to verify your account.",
      );
      setPersonal(initialPersonal);
      setBusiness(initialBusiness);
      setAcceptedTerms(false);

      window.setTimeout(() => navigate("/app/login"), 1500);
    } catch {
      // API error surfaced through context
    }
  };

  const validate = () => {
    if (!acceptedTerms)
      return "You must accept the Terms & Conditions and Privacy Policy.";

    if (activeTab === "renter" || activeTab === "personal") {
      if (!personal.fullName.trim()) return "Full name is required.";
      if (!EMAIL_REGEX.test(personal.email.trim().toLowerCase()))
        return "Please enter a valid email address.";
      if (!PASSWORD_REGEX.test(personal.password))
        return "Password must be 8+ characters and include at least one letter and one number.";
      if (personal.password !== personal.confirmPassword)
        return "Passwords do not match.";
      if (!personal.address.trim()) return "Address is required.";
    } else {
      if (!business.businessName.trim()) return "Business name is required.";
      if (!EMAIL_REGEX.test(business.email.trim().toLowerCase()))
        return "Please enter a valid email address.";
      if (!PASSWORD_REGEX.test(business.password))
        return "Password must be 8+ characters and include at least one letter and one number.";
      if (business.password !== business.confirmPassword)
        return "Passwords do not match.";
      if (!business.businessAddress.trim())
        return "Business address is required.";
      if (!business.registrationNumber.trim())
        return "Registration number is required.";
      if (!business.taxId.trim()) return "Tax ID is required.";
    }
    return null;
  };

  const onSubmit = async (event) => {
    event.preventDefault();
    clearError();
    setLocalError("");
    setSuccess("");

    const validationError = validate();
    if (validationError) {
      setLocalError(validationError);
      return;
    }

    setIsConfirmOpen(true);
  };

  const isPersonalTab = activeTab === "renter" || activeTab === "personal";
  const confirmation = roleConfirmationCopy[activeTab];

  return (
    <main className="app-surface">
      <section className="auth-card auth-card-wide">
        <div className="auth-brand">
          <img
            src={process.env.PUBLIC_URL + "/assets/logo/LogoH.png"}
            alt="Rental Highway"
            className="auth-brand-logo"
          />
        </div>
        <h1>Create account</h1>
        <p>Your account must be verified by email before first login.</p>

        {/* ── Role tabs ── */}
        <div className="signup-tabs">
          {TABS.map((tab) => (
            <button
              key={tab.id}
              type="button"
              className={`renter-tab${activeTab === tab.id ? " active" : ""}`}
              onClick={() => switchTab(tab.id)}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <form onSubmit={onSubmit} className="auth-form">
          {/* ── Renter / Personal Owner fields ── */}
          {isPersonalTab && (
            <>
              <label htmlFor="fullName">Full Name</label>
              <input
                id="fullName"
                name="fullName"
                value={personal.fullName}
                onChange={onChangePersonal}
                disabled={loading}
                autoComplete="name"
              />

              <label htmlFor="email">Email Address</label>
              <input
                id="email"
                name="email"
                type="email"
                value={personal.email}
                onChange={onChangePersonal}
                disabled={loading}
                autoComplete="email"
              />

              <label htmlFor="password">Password</label>
              <input
                id="password"
                name="password"
                type="password"
                value={personal.password}
                onChange={onChangePersonal}
                disabled={loading}
                autoComplete="new-password"
              />

              <label htmlFor="confirmPassword">Confirm Password</label>
              <input
                id="confirmPassword"
                name="confirmPassword"
                type="password"
                value={personal.confirmPassword}
                onChange={onChangePersonal}
                disabled={loading}
                autoComplete="new-password"
              />

              <label htmlFor="phone">Phone (Optional)</label>
              <input
                id="phone"
                name="phone"
                type="tel"
                value={personal.phone}
                onChange={onChangePersonal}
                disabled={loading}
                autoComplete="tel"
              />

              <label htmlFor="address">Residential Address</label>
              <input
                id="address"
                name="address"
                value={personal.address}
                onChange={onChangePersonal}
                disabled={loading}
                autoComplete="street-address"
              />

              <label htmlFor="dob">Date Of Birth (Optional)</label>
              <input
                id="dob"
                name="dob"
                type="date"
                value={personal.dob}
                onChange={onChangePersonal}
                disabled={loading}
              />
            </>
          )}

          {/* ── Business Owner fields ── */}
          {activeTab === "business" && (
            <>
              <label htmlFor="businessName">Business Name</label>
              <input
                id="businessName"
                name="businessName"
                value={business.businessName}
                onChange={onChangeBusiness}
                disabled={loading}
              />

              <label htmlFor="bizEmail">Email Address</label>
              <input
                id="bizEmail"
                name="email"
                type="email"
                value={business.email}
                onChange={onChangeBusiness}
                disabled={loading}
                autoComplete="email"
              />

              <label htmlFor="bizPassword">Password</label>
              <input
                id="bizPassword"
                name="password"
                type="password"
                value={business.password}
                onChange={onChangeBusiness}
                disabled={loading}
                autoComplete="new-password"
              />

              <label htmlFor="bizConfirmPassword">Confirm Password</label>
              <input
                id="bizConfirmPassword"
                name="confirmPassword"
                type="password"
                value={business.confirmPassword}
                onChange={onChangeBusiness}
                disabled={loading}
                autoComplete="new-password"
              />

              <label htmlFor="businessAddress">Business Address</label>
              <input
                id="businessAddress"
                name="businessAddress"
                value={business.businessAddress}
                onChange={onChangeBusiness}
                disabled={loading}
              />

              <label htmlFor="registrationNumber">Registration Number</label>
              <input
                id="registrationNumber"
                name="registrationNumber"
                value={business.registrationNumber}
                onChange={onChangeBusiness}
                disabled={loading}
              />

              <label htmlFor="taxId">Tax Identification Number</label>
              <input
                id="taxId"
                name="taxId"
                value={business.taxId}
                onChange={onChangeBusiness}
                disabled={loading}
              />
            </>
          )}

          {/* ── Terms checkbox ── */}
          <div className="signup-terms-row">
            <input
              id="acceptTerms"
              type="checkbox"
              className="signup-terms-checkbox"
              checked={acceptedTerms}
              onChange={(e) => setAcceptedTerms(e.target.checked)}
              disabled={loading}
            />
            <label htmlFor="acceptTerms" className="signup-terms-label">
              I agree to the{" "}
              <a
                href="https://rentalhighway-documents.s3.us-east-1.amazonaws.com/Terms+and+Conditions+-+Rental+Highway.pdf"
                target="_blank"
                rel="noopener noreferrer"
              >
                Terms &amp; Conditions
              </a>{" "}
              and{" "}
              <a
                href="https://rentalhighway-documents.s3.us-east-1.amazonaws.com/Privacy+Policy+-+Rental+Highway.pdf"
                target="_blank"
                rel="noopener noreferrer"
              >
                Privacy Policy
              </a>
            </label>
          </div>

          <button
            type="submit"
            className="btn btn-primary"
            disabled={loading || !acceptedTerms}
          >
            {loading ? "Creating account…" : "Create account"}
          </button>
        </form>

        {success && <p className="form-success">{success}</p>}
        {(localError || error) && (
          <p className="form-error">{localError || error}</p>
        )}

        <p className="auth-footnote">
          Already have an account? <Link to="/app/login">Sign in</Link>
        </p>
      </section>

      {isConfirmOpen && (
        <div
          className="signup-confirm-overlay"
          role="presentation"
          onClick={closeConfirmation}
        >
          <div
            className="signup-confirm-dialog"
            role="dialog"
            aria-modal="true"
            aria-labelledby="signup-confirm-title"
            aria-describedby="signup-confirm-description"
            onClick={(event) => event.stopPropagation()}
          >
            <p className="signup-confirm-kicker">Confirm account type</p>
            <h2 id="signup-confirm-title">{confirmation.title}</h2>
            <p id="signup-confirm-description">{confirmation.description}</p>
            <div className="signup-confirm-actions">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={closeConfirmation}
                disabled={loading}
              >
                Go back
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={submitRegistration}
                disabled={loading}
              >
                {loading ? "Creating account…" : confirmation.confirmLabel}
              </button>
            </div>
          </div>
        </div>
      )}
    </main>
  );
}
